import type { FrontierClientOptions } from '@raystack/frontier/react';

import {
  CORRELATION_ID_HEADER,
  EMAILS,
  PLAN_DOWNGRADE_MESSAGES
} from '@src/utils/constants/APP';
import { US_DATE_FORMAT, US_SHORT_DATE_FORMAT } from '@src/utils/dateUtils';
import { generateRequestId } from '@src/utils/requestId';

const showBasePlan = import.meta.env.VITE_BILLING_SHOW_BASE_PLAN === 'true';
const APP_NAME = 'Aurora';

const getFrontierConfig = (): FrontierClientOptions => {
  const frontierEndpoint = import.meta.env.VITE_FRONTIER_URL;
  const frontierConnectEndpoint = import.meta.env.VITE_FRONTIER_CONNECT_URL;

  const currentHost = window?.location?.origin || 'http://localhost:3000';
  return {
    connectEndpoint: frontierConnectEndpoint,
    endpoint: frontierEndpoint,
    redirectLogin: `${currentHost}/login`,
    redirectSignup: `${currentHost}/signup`,
    redirectMagicLinkVerify: `${currentHost}/magiclink-verify`,
    callbackUrl: `${currentHost}/callback`,
    dateFormat: US_DATE_FORMAT,
    shortDateFormat: US_SHORT_DATE_FORMAT,
    billing: {
      supportEmail: EMAILS.BILLING,
      successUrl: `${currentHost}/payment/success`,
      cancelUrl: `${currentHost}/payment/cancel`,
      showPerMonthPrice: true,
      cancelAfterTrial: true,
      basePlan: showBasePlan
        ? {
            title: 'Standard Plan',
            name: 'standard_plan_plan',
            description: 'Order images',
            features: {
              'AOI size for tasking': '5000 km²',
              'AOI size for analysis': '200 km²',
              'Wallet Recharge': 'No Limit',
              'Task Pixxel Satellites*': 'true',
              'Open Source Data': 'true',
              'Order Archive Images*': 'true',
              'Image Exploration Tools': 'true',
              Indices: 'true',
              'Model Insights*': 'true',
              'Workflows*': 'true',
              'Aurora Intelligence (AI assistant)': 'true',
              'Audit Logs': 'Coming Soon'
            }
          }
        : undefined
    },
    customization: {
      terminology: {
        organization: {
          singular: 'Workspace',
          plural: 'Workspaces'
        },
        appName: APP_NAME
      },
      messages: {
        billing: {
          plan_change: {
            'yearly_enterprise_plan_plan:standard_plan_plan':
              PLAN_DOWNGRADE_MESSAGES.enterprise_to_standard,
            'monthly_enterprise_plan_plan:standard_plan_plan':
              PLAN_DOWNGRADE_MESSAGES.enterprise_to_standard,
            'yearly_enterprise_plan_plan:monthly_professional_plan_plan':
              PLAN_DOWNGRADE_MESSAGES.enterprise_to_professional,
            'monthly_enterprise_plan_plan:monthly_professional_plan_plan':
              PLAN_DOWNGRADE_MESSAGES.enterprise_to_professional,
            'yearly_enterprise_plan_plan:yearly_professional_plan_plan':
              PLAN_DOWNGRADE_MESSAGES.enterprise_to_professional,
            'monthly_enterprise_plan_plan:yearly_professional_plan_plan':
              PLAN_DOWNGRADE_MESSAGES.enterprise_to_professional,
            'monthly_professional_plan_plan:standard_plan_plan':
              PLAN_DOWNGRADE_MESSAGES.professional_to_standard,
            'yearly_professional_plan_plan:standard_plan_plan':
              PLAN_DOWNGRADE_MESSAGES.professional_to_standard
          }
        }
      }
    }
  };
};

export const customHeaders = {
  [CORRELATION_ID_HEADER]: generateRequestId
};

export const frontierConfig = getFrontierConfig();
